import { createContext, type ReactNode, useContext } from 'react';
import { type Held, MONIES, THEMES, useLook } from '../lib/look.ts';
import { PageSection, Row, Rows } from './pages/Sections.tsx';
import { Segmented } from './Toolbar.tsx';

const LookContext = createContext<Held | null>(null);

export function LookProvider({ children }: { children: ReactNode }) {
  return <LookContext.Provider value={useLook()}>{children}</LookContext.Provider>;
}

function Choice<T extends string>({
  label,
  hint,
  options,
  labels,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  options: readonly T[];
  labels?: Record<T, string>;
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <Row
      title={label}
      hint={hint}
      control={
        <Segmented
          label={label}
          className="shrink-0 bg-canvas"
          onClassName="bg-surface-raised"
          options={options}
          labels={labels}
          value={value}
          onChange={onChange}
        />
      }
    />
  );
}

export function LookSection() {
  const held = useContext(LookContext);
  if (held === null) throw new Error('LookSection needs a LookProvider above it');
  const { look, device, set } = held;

  return (
    <PageSection id="appearance" title="Appearance" blurb="This device only.">
      <Rows>
        <Choice
          label="Theme"
          hint={`Auto follows this device: ${device} now.`}
          options={THEMES}
          value={look.theme}
          onChange={(theme) => set({ theme })}
        />
        <Choice
          label="Up and down"
          hint="Blue and red is for red–green colour blindness."
          options={MONIES}
          labels={{ 'green-red': 'Green and red', 'blue-red': 'Blue and red' }}
          value={look.money}
          onChange={(money) => set({ money })}
        />
      </Rows>
    </PageSection>
  );
}
